import { makeStyles } from '@material-ui/core/styles'

const primary = '#4fc0e8'
const primaryDark = '#3aaad0'
const textPrimary = '#333'
const textSecondary = '#666'
const textMuted = '#999'
const border = '#e0e0e0'
const borderLight = '#eee'
const bgWhite = '#fff'
const bgGray = '#f5f5f5'
const errorColor = '#d32f2f'
const errorBg = '#fde8e8'
const warnColor = '#f57c00'
const warnBg = '#fff3e0'
const infoColor = '#0288d1'
const infoBg = '#e1f5fe'
const debugColor = '#757575'
const debugBg = '#f5f5f5'

export default makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 96px)',
    overflow: 'hidden',
    fontFamily: 'CooperHewitt, sans-serif',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    marginBottom: 12,
    flexWrap: 'wrap',
    gap: 8,
    flexShrink: 0,
  },
  pageTitle: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: textPrimary,
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    color: textSecondary,
    fontWeight: 400,
    margin: 0,
    fontSize: 13,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  freshnessText: {
    fontSize: 12,
    color: textMuted,
    whiteSpace: 'nowrap',
  },
  exportWrapper: {
    position: 'relative',
  },
  exportButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: bgWhite,
    color: textPrimary,
    border: `1px solid ${border}`,
    padding: '6px 14px',
    borderRadius: 4,
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    transition: 'background 150ms',
    '&:hover': { backgroundColor: bgGray },
  },
  exportDropdown: {
    position: 'absolute',
    right: 0,
    top: '100%',
    marginTop: 4,
    width: 180,
    backgroundColor: bgWhite,
    border: `1px solid ${border}`,
    borderRadius: 4,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    zIndex: 60,
    padding: '4px 0',
  },
  exportItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 14px',
    fontSize: 13,
    color: textPrimary,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    width: '100%',
    textAlign: 'left',
    transition: 'background 150ms',
    '&:hover': { backgroundColor: bgGray },
  },
  refreshButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: primary,
    color: '#fff',
    border: 'none',
    padding: '6px 14px',
    borderRadius: 4,
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
    transition: 'background 150ms, transform 100ms',
    '&:hover': { backgroundColor: primaryDark },
    '&:active': { backgroundColor: primaryDark, transform: 'scale(0.96)' },
    '&:disabled': { opacity: 0.7, cursor: 'default' },
  },
  '@keyframes refreshSpin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  },
  refreshIconSpinning: {
    animation: '$refreshSpin 0.8s linear infinite',
  },
  toolbar: {
    backgroundColor: bgGray,
    padding: 12,
    borderRadius: 4,
    marginBottom: 12,
    border: `1px solid ${borderLight}`,
    flexShrink: 0,
  },
  toolbarRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
    alignItems: 'center',
  },
  searchWrapper: {
    flex: 1,
    minWidth: 220,
    position: 'relative',
  },
  searchIcon: {
    position: 'absolute',
    left: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    color: textMuted,
    fontSize: 18,
  },
  searchInput: {
    width: '100%',
    backgroundColor: bgWhite,
    border: `1px solid ${border}`,
    borderRadius: 4,
    padding: '7px 34px 7px 34px',
    fontSize: 13,
    color: textPrimary,
    outline: 'none',
    transition: 'border-color 150ms',
    '&:focus': { borderColor: primary },
    '&::placeholder': { color: textMuted },
  },
  searchClearButton: {
    position: 'absolute',
    right: 6,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    border: 'none',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    color: textMuted,
    cursor: 'pointer',
    padding: 0,
    transition: 'background 150ms, color 150ms',
    '&:hover': { backgroundColor: bgGray, color: textPrimary },
  },
  searchClearIcon: {
    fontSize: 16,
  },
  levelToggles: {
    display: 'flex',
    backgroundColor: bgWhite,
    border: `1px solid ${border}`,
    padding: 2,
    borderRadius: 4,
  },
  levelToggle: {
    padding: '4px 12px',
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 3,
    border: 'none',
    cursor: 'pointer',
    transition: 'background 150ms, color 150ms',
    backgroundColor: 'transparent',
    color: textSecondary,
    '&:hover': { backgroundColor: bgGray },
  },
  levelToggleActive: {
    backgroundColor: `${primary} !important`,
    color: '#fff !important',
  },
  levelToggleError: { color: `${errorColor} !important` },
  levelToggleInfo: { color: `${infoColor} !important` },
  levelToggleWarn: { color: `${warnColor} !important` },

  sortButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    backgroundColor: bgWhite,
    padding: '6px 10px',
    borderRadius: 4,
    fontSize: 13,
    border: `1px solid ${border}`,
    cursor: 'pointer',
    color: textSecondary,
    transition: 'background 150ms',
    '&:hover': { backgroundColor: bgGray },
  },
  clearFiltersButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'transparent',
    padding: '4px 10px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    border: `1px solid ${border}`,
    cursor: 'pointer',
    color: textMuted,
    transition: 'all 150ms',
    '&:hover': { backgroundColor: bgGray, color: textPrimary },
  },

  filtersRow: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
    paddingTop: 8,
    marginTop: 8,
    borderTop: `1px solid ${borderLight}`,
  },
  filtersLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    fontWeight: 600,
    color: textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  },
  filterChips: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },
  filterChip: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 10px',
    backgroundColor: bgWhite,
    color: textSecondary,
    fontSize: 12,
    borderRadius: 3,
    border: `1px solid ${border}`,
    cursor: 'pointer',
    transition: 'border-color 150ms',
    '&:hover': { borderColor: primary },
  },
  // Shared always-visible dropdown arrow, overlaid on the trigger's right
  // padding. pointer-events none so clicks fall through to the trigger
  // underneath.
  filterChipArrow: {
    position: 'absolute',
    right: 5,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 16,
    color: textMuted,
    pointerEvents: 'none',
  },
  filterChipIcon: {
    fontSize: 14,
    color: textMuted,
  },
  // Active trace-correlation chip: highlighted so it reads as a distinct,
  // temporary "pinned to one trace" mode, with its own clear affordance.
  traceChip: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    maxWidth: 320,
    padding: '3px 8px',
    backgroundColor: infoBg,
    color: primaryDark,
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 3,
    border: `1px solid ${primary}`,
  },
  traceChipLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: "'Roboto Mono', monospace",
  },
  traceChipClear: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: 'none',
    background: 'none',
    padding: 0,
    color: primaryDark,
    cursor: 'pointer',
    '&:hover': { color: errorColor },
  },
  columnSettingsPopup: {
    width: 220,
    backgroundColor: bgWhite,
    border: `1px solid ${border}`,
    borderRadius: 4,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    padding: 8,
    fontFamily: 'CooperHewitt, sans-serif',
  },
  columnSettingsHeader: {
    display: 'flex',
    gap: 14,
    padding: '2px 2px 8px',
  },
  columnSettingsLink: {
    border: 'none',
    background: 'none',
    padding: 0,
    fontSize: 12,
    fontWeight: 600,
    fontFamily: 'inherit',
    color: infoColor,
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
  // Right-aligned in the header row, muted until hovered so the destructive
  // action reads as secondary next to Show All / Hide All.
  columnSettingsReset: {
    marginLeft: 'auto',
    color: textMuted,
    '&:hover': { color: textPrimary, textDecoration: 'underline' },
  },
  columnSettingsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  columnSettingsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '7px 8px',
    backgroundColor: bgWhite,
    border: `1px solid ${border}`,
    borderRadius: 4,
    transition: 'border-color 150ms',
    '&:hover': { borderColor: primary },
  },
  columnSettingsToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    minWidth: 0,
    border: 'none',
    background: 'none',
    padding: 0,
    fontSize: 12,
    fontFamily: 'inherit',
    color: textPrimary,
    cursor: 'pointer',
    textAlign: 'left' as const,
    '&[data-checked] $columnSettingsBox': {
      backgroundColor: infoColor,
      borderColor: infoColor,
    },
    '&[data-unchecked] $columnSettingsLabel': {
      color: textMuted,
    },
  },
  columnSettingsHandle: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    color: textMuted,
    '&:hover': { color: textPrimary },
  },
  columnSettingsBox: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    flexShrink: 0,
    border: `1.5px solid ${border}`,
    borderRadius: 3,
    color: '#fff',
    transition: 'background 150ms, border-color 150ms',
  },
  columnSettingsIndicator: {
    display: 'flex',
  },
  columnSettingsLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    alignItems: 'center',
    marginTop: 8,
    '&:empty': { display: 'none' },
  },
  comboboxChips: {
    display: 'contents',
  },
  comboboxTrigger: {
    fontFamily: 'inherit',
    paddingRight: 22,
  },
  comboboxPositioner: {
    zIndex: 2000,
    outline: 'none',
  },
  comboboxContent: {
    width: 240,
    backgroundColor: bgWhite,
    border: `1px solid ${border}`,
    borderRadius: 4,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    overflow: 'hidden',
    fontFamily: 'CooperHewitt, sans-serif',
  },
  comboboxInputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderBottom: `1px solid ${borderLight}`,
  },
  comboboxSearchIcon: {
    fontSize: 16,
    color: textMuted,
  },
  comboboxInput: {
    flex: 1,
    minWidth: 0,
    border: 'none',
    outline: 'none',
    fontSize: 12,
    color: textPrimary,
    backgroundColor: 'transparent',
    '&::placeholder': { color: textMuted },
  },
  comboboxList: {
    maxHeight: 240,
    overflowY: 'auto',
    padding: 4,
  },
  comboboxEmpty: {
    padding: '10px 8px',
    fontSize: 12,
    color: textMuted,
    textAlign: 'center' as const,
  },
  comboboxItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '5px 8px',
    fontSize: 12,
    color: textSecondary,
    borderRadius: 3,
    cursor: 'pointer',
    '&[data-highlighted]': {
      backgroundColor: bgGray,
      color: textPrimary,
    },
  },
  comboboxCheck: {
    fontSize: 14,
    color: primary,
    flexShrink: 0,
  },
  comboboxItemLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dateRangeRow: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    paddingTop: 8,
    marginTop: 8,
    borderTop: `1px solid ${borderLight}`,
  },
  dateRangeLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  },
  dateInput: {
    backgroundColor: bgWhite,
    border: `1px solid ${border}`,
    borderRadius: 4,
    padding: '5px 8px',
    fontSize: 12,
    color: textPrimary,
    outline: 'none',
    transition: 'border-color 150ms',
    '&:focus': { borderColor: primary },
  },
  dateRangeSeparator: {
    fontSize: 12,
    color: textMuted,
  },
  tableContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    border: `1px solid ${border}`,
    borderRadius: 4,
    backgroundColor: bgWhite,
    overflow: 'hidden',
  },
  logGrid: {
    display: 'grid',
    gridTemplateColumns: '145px 55px 110px 95px 105px 130px 1fr',
    alignItems: 'center',
    // Left padding on every cell (header labels + row data alike) so text never
    // hugs the resize divider sitting on the previous column's right edge.
    // The level strip is excluded: with border-box sizing the 10px padding
    // would force it to render 10px wide no matter its width value.
    '& > div:not($logRowStrip)': {
      paddingLeft: 10,
    },
  },
  logStreamHeader: {
    padding: '6px 16px',
    fontSize: 10,
    fontWeight: 700,
    color: textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.1em',
    backgroundColor: bgGray,
    borderBottom: `1px solid ${border}`,
    flexShrink: 0,
  },
  logHeaderCell: {
    position: 'relative',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerSortButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    maxWidth: '100%',
    padding: 0,
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
    textTransform: 'inherit' as const,
    letterSpacing: 'inherit',
    transition: 'color 120ms',
    '&:hover': { color: textSecondary },
  },
  headerSortLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerSortArrows: {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0,
  },
  headerSortArrow: {
    height: 7,
    opacity: 0.35,
  },
  headerSortArrowActive: {
    color: primary,
    opacity: 1,
  },
  resizer: {
    position: 'absolute',
    top: 0,
    right: 0,
    height: '100%',
    width: 9,
    cursor: 'col-resize',
    userSelect: 'none',
    touchAction: 'none',
    borderRight: `2px solid ${border}`,
    transition: 'border-color 120ms',
    '&:hover': { borderRightColor: primary },
  },
  resizerActive: {
    borderRightColor: `${primary} !important`,
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  logList: {
    display: 'flex',
    flexDirection: 'column',
  },
  logRow: {
    padding: '8px 16px',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    transition: 'background 100ms',
    borderBottom: `1px solid ${borderLight}`,
  },
  logRowEven: {
    backgroundColor: bgWhite,
    '&:hover': { backgroundColor: bgGray },
  },
  logRowOdd: {
    backgroundColor: '#fafafa',
    '&:hover': { backgroundColor: bgGray },
  },
  logRowStrip: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: 3,
  },
  logRowStripError: { backgroundColor: errorColor },
  logRowStripWarn: { backgroundColor: warnColor },
  logRowStripInfo: { backgroundColor: infoColor },
  logRowStripDebug: { backgroundColor: debugColor },
  logTimestamp: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: 11,
    color: textSecondary,
  },
  levelChip: {
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 2,
    fontSize: 10,
    fontWeight: 700,
  },
  levelChipError: {
    backgroundColor: errorBg,
    color: errorColor,
  },
  levelChipWarn: {
    backgroundColor: warnBg,
    color: warnColor,
  },
  levelChipInfo: {
    backgroundColor: infoBg,
    color: infoColor,
  },
  levelChipDebug: {
    backgroundColor: debugBg,
    color: debugColor,
  },
  logSource: {
    fontSize: 11,
    fontWeight: 600,
    color: textSecondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  logMachine: {
    fontSize: 11,
    color: textMuted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: "'Roboto Mono', monospace",
  },
  logConnection: {
    fontSize: 11,
    color: textMuted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: "'Roboto Mono', monospace",
  },
  logState: {
    fontSize: 11,
    color: textMuted,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  logMessage: {
    fontSize: 12,
    color: textPrimary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  logMessageCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    overflow: 'hidden',
  },
  logMessageText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  highlightMark: {
    backgroundColor: '#fff3b0',
    color: 'inherit',
    padding: '0 1px',
    borderRadius: 2,
  },
  logUser: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
  },
  userChip: {
    display: 'inline-block',
    flexShrink: 0,
    maxWidth: 140,
    padding: '1px 6px',
    borderRadius: 2,
    fontSize: 10,
    fontWeight: 700,
    backgroundColor: infoBg,
    color: infoColor,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userChipSystem: {
    backgroundColor: bgGray,
    color: textMuted,
  },
  logDetail: {
    backgroundColor: '#fafafa',
    borderLeft: `3px solid ${errorColor}`,
    marginLeft: 16,
    padding: 16,
    borderBottom: `1px solid ${borderLight}`,
  },
  logDetailNonError: {
    borderLeftColor: primary,
  },
  logDetailWarn: {
    borderLeftColor: warnColor,
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 16,
    marginBottom: 12,
  },
  detailLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: textMuted,
    textTransform: 'uppercase' as const,
    marginBottom: 2,
  },
  detailValue: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: 12,
    color: textPrimary,
    wordBreak: 'break-all',
  },
  detailValueRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 4,
  },
  copyButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: 'none',
    background: 'none',
    padding: 2,
    borderRadius: 3,
    color: textMuted,
    cursor: 'pointer',
    transition: 'background 150ms, color 150ms',
    '&:hover': { backgroundColor: bgGray, color: primary },
  },
  traceFilterButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    flexShrink: 0,
    border: `1px solid ${border}`,
    background: bgWhite,
    padding: '1px 7px',
    borderRadius: 3,
    fontSize: 11,
    fontWeight: 600,
    color: primaryDark,
    cursor: 'pointer',
    transition: 'background 150ms, border-color 150ms',
    '&:hover': { backgroundColor: infoBg, borderColor: primary },
  },
  exceptionBlock: {
    backgroundColor: errorBg,
    border: `1px solid #f5c6cb`,
    borderRadius: 4,
    padding: 10,
    marginBottom: 8,
  },
  exceptionClass: {
    fontSize: 12,
    fontWeight: 700,
    color: errorColor,
    marginBottom: 2,
  },
  exceptionMessage: {
    fontSize: 12,
    color: textPrimary,
  },
  messageBlock: {
    backgroundColor: bgWhite,
    border: `1px solid ${border}`,
    borderRadius: 4,
    padding: 10,
    marginBottom: 8,
    fontSize: 12,
    color: textPrimary,
  },
  messageBlockWarn: {
    backgroundColor: warnBg,
    borderColor: '#ffcc80',
  },
  messageBlockInfo: {
    backgroundColor: infoBg,
    borderColor: '#81d4fa',
  },
  messageBlockDebug: {
    backgroundColor: debugBg,
  },
  detailPayload: {
    backgroundColor: bgWhite,
    padding: 12,
    borderRadius: 4,
    fontFamily: "'Roboto Mono', monospace",
    fontSize: 11,
    color: textSecondary,
    overflowX: 'auto',
    lineHeight: 1.6,
    border: `1px solid ${border}`,
  },
  payloadHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  payloadActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  payloadTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: textMuted,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  payloadToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'transparent',
    border: `1px solid ${border}`,
    borderRadius: 4,
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 600,
    color: textSecondary,
    cursor: 'pointer',
    transition: 'background 150ms, color 150ms',
    '&:hover': { backgroundColor: bgGray, color: textPrimary },
  },
  jsonTree: {
    backgroundColor: bgWhite,
    padding: '8px 10px',
    borderRadius: 4,
    border: `1px solid ${border}`,
    fontFamily: "'Roboto Mono', monospace",
    fontSize: 12,
    lineHeight: 1.7,
    overflowX: 'auto',
  },
  jsonRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 2,
    borderRadius: 3,
  },
  jsonBranchRow: {
    cursor: 'pointer',
    '&:hover': { backgroundColor: bgGray },
  },
  jsonToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    color: textMuted,
    marginLeft: -4,
  },
  jsonKey: {
    color: textPrimary,
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  jsonColon: {
    color: textMuted,
    marginRight: 6,
  },
  jsonSummary: {
    color: textMuted,
    fontStyle: 'italic',
  },
  jsonString: {
    color: '#0a7f3f',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  jsonNumber: {
    color: infoColor,
  },
  jsonBool: {
    color: warnColor,
    fontWeight: 600,
  },
  jsonNull: {
    color: textMuted,
    fontStyle: 'italic',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 4,
    padding: '8px 0',
    borderTop: `1px solid ${border}`,
    backgroundColor: bgGray,
    flexShrink: 0,
  },
  pageButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 500,
    transition: 'background 150ms',
    height: 32,
    minWidth: 32,
    padding: '0 10px',
    border: 'none',
    cursor: 'pointer',
    color: textSecondary,
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: bgWhite },
  },
  pageButtonActive: {
    border: `1px solid ${border}`,
    color: textPrimary,
    backgroundColor: bgWhite,
    fontWeight: 600,
  },
  pageButtonDisabled: {
    opacity: 0.4,
    cursor: 'default',
    '&:hover': { backgroundColor: 'transparent' },
  },
  pageEllipsis: {
    display: 'flex',
    height: 32,
    width: 32,
    alignItems: 'center',
    justifyContent: 'center',
    color: textMuted,
  },
  pageSize: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginRight: 12,
    fontSize: 13,
    color: textSecondary,
  },
  pageSizeSelect: {
    height: 32,
    padding: '0 8px',
    borderRadius: 4,
    border: `1px solid ${border}`,
    backgroundColor: bgWhite,
    fontSize: 13,
    color: textPrimary,
    cursor: 'pointer',
    outline: 'none',
    transition: 'border-color 150ms',
    '&:focus': { borderColor: primary },
  },
  goToPage: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginLeft: 12,
    fontSize: 13,
    color: textSecondary,
  },
  goToPageInput: {
    height: 32,
    width: 64,
    padding: '0 12px',
    borderRadius: 4,
    border: `1px solid ${border}`,
    backgroundColor: bgWhite,
    fontSize: 13,
    color: textPrimary,
    textAlign: 'center',
    outline: 'none',
    transition: 'border-color 150ms',
    '&:focus': { borderColor: primary },
    // Hide the native number spinners for a clean pill.
    '&::-webkit-outer-spin-button': { WebkitAppearance: 'none', margin: 0 },
    '&::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
    '-moz-appearance': 'textfield',
  },
  '@keyframes skeletonPulse': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0.45 },
    '100%': { opacity: 1 },
  },
  skeletonRow: {
    padding: '8px 16px',
    borderBottom: `1px solid ${borderLight}`,
  },
  skeletonBar: {
    height: 12,
    borderRadius: 3,
    backgroundColor: border,
    animation: '$skeletonPulse 1.4s ease-in-out infinite',
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 0',
    color: errorColor,
    flexDirection: 'column',
    gap: 10,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 0',
    color: textSecondary,
    gap: 8,
  },
})
